import React, { useEffect, useState } from "react";
import "./CustomerReviews.scss";
import {
  Row,
  Col,
  Rate,
  Button,
  List,
  Flex,
  Avatar,
  Image,
  Empty,
} from "antd";
import Helpers from "@utils/Helpers";
import { errorLog } from "logging";
import { feedbackApi } from "services/FeedbackApi";
import type { FeedbackOut, RatingSummary } from "services/FeedbackApi";

type PaginationPosition = "top" | "bottom" | "both";

type PaginationAlign = "start" | "center" | "end";

interface ICustomerReviews {
  productId: number;
}

const CustomerReviews: React.FC<ICustomerReviews> = ({ productId }) => {
  const [activeRating, setActiveRating] = useState(0);
  const [summary, setSummary] = useState<RatingSummary | null>(null);
  const [feedbackList, setFeedbackList] = useState<FeedbackOut[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const pageSize = 10;
  const position: PaginationPosition = "bottom";
  const align: PaginationAlign = "end";

  useEffect(() => {
    const fetchSummary = async () => {
      if (!productId) {
        return;
      }
      try {
        const response = await feedbackApi.getProductFeedbackSummary(productId);
        if (response.status === 200) {
          setSummary(response.data);
        }
      } catch (error) {
        errorLog("CustomerReviews.tsx - fetchSummary", error);
      }
    };
    fetchSummary();
  }, [productId]);

  useEffect(() => {
    const fetchFeedback = async () => {
      if (!productId) {
        return;
      }
      setIsLoading(true);
      try {
        const response = await feedbackApi.getProductFeedback(productId, {
          star: activeRating || undefined,
          page,
          page_size: pageSize,
        });
        if (response.status === 200) {
          setFeedbackList(response.data.items ?? []);
          setTotal(response.data.total ?? 0);
        }
      } catch (error) {
        errorLog("CustomerReviews.tsx - fetchFeedback", error);
        setFeedbackList([]);
        setTotal(0);
      } finally {
        setIsLoading(false);
      }
    };
    fetchFeedback();
  }, [productId, activeRating, page, pageSize]);

  const selectRating = (star: number) => {
    setPage(1);
    setActiveRating(star);
  };

  const ratingGroup = [
    { id: 0, title: `All (${summary?.total ?? 0})` },
    { id: 1, title: `1 Star (${summary?.by_star?.[1] ?? 0})` },
    { id: 2, title: `2 Stars (${summary?.by_star?.[2] ?? 0})` },
    { id: 3, title: `3 Stars (${summary?.by_star?.[3] ?? 0})` },
    { id: 4, title: `4 Stars (${summary?.by_star?.[4] ?? 0})` },
    { id: 5, title: `5 Stars (${summary?.by_star?.[5] ?? 0})` },
  ];

  const detailRatings = [
    { label: "Quality", value: summary?.detail.quality ?? 0 },
    { label: "Shipping Cost", value: summary?.detail.shipping_cost ?? 0 },
    { label: "Shipping Time", value: summary?.detail.shipping_time ?? 0 },
    { label: "Service", value: summary?.detail.service ?? 0 },
  ];

  return (
    <div className="CustomerReviews">
      <div className="section-title mb-2">Customer Reviews</div>
      <div className="total-rating">
        <Rate disabled value={summary?.avg_rating ?? 0} />{" "}
        {(summary?.avg_rating ?? 0).toFixed(1)}
        <span className="ms-1">({summary?.total ?? 0} reviews)</span>
      </div>
      <Row className="mt-4">
        <Col span={10}>
          <div className="rating-view-control">
            <div className="buttons-rating">
              {ratingGroup.map((item) => {
                return (
                  <Button
                    key={item.id}
                    className={`rating-btn ${
                      activeRating === item.id && "active"
                    }`}
                    onClick={() => selectRating(item.id)}
                  >
                    {item.title}
                  </Button>
                );
              })}
            </div>
            <div className="details-rating">
              <div className="title">Detailed Ratings:</div>
              <div className="detail-group">
                {detailRatings.map((item) => {
                  return (
                    <div className="detail-item" key={item.label}>
                      <div className="detail-title">{item.label}</div>
                      <div className="detail-value">
                        <Rate disabled value={item.value} />{" "}
                        {item.value.toFixed(1)}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </Col>
        <Col span={14}>
          <List
            pagination={{
              position,
              align,
              current: page,
              pageSize,
              total,
              onChange: (nextPage) => setPage(nextPage),
            }}
            className="px-3"
            loading={isLoading}
            dataSource={feedbackList}
            locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
            renderItem={(item) => (
              <List.Item>
                <Flex flex={1} gap={12}>
                  <Avatar src={item.avatar_url || undefined} size={70}>
                    {item.consumer_name
                      ? item.consumer_name.charAt(0).toUpperCase()
                      : "?"}
                  </Avatar>
                  <div className="customer-info">
                    <div className="customer-name">
                      {item.consumer_name || "Anonymous"}
                    </div>
                    <div className="customer-rating">
                      <Rate disabled value={item.rating} />
                    </div>
                    <div className="customer-post-date">
                      {item.created_at ? Helpers.formatDateTime(item.created_at) : ""}
                    </div>
                  </div>
                  <div className="feedback-group">
                    <div className="feedback-content">{item.comment}</div>
                    {item.images.length > 0 && (
                      <Flex gap={12} className="mt-2">
                        {item.images.map((image, index) => {
                          return (
                            <Image
                              className="feedback-image"
                              src={image}
                              height={90}
                              key={index}
                            />
                          );
                        })}
                      </Flex>
                    )}
                  </div>
                </Flex>
              </List.Item>
            )}
          />
        </Col>
      </Row>
    </div>
  );
};

export default CustomerReviews;
